/**
 * @file Reads Firestore documents by path through one `useStoreDocsByPaths`
 * call, so this call is every path's first connector and no error is lost.
 * Consumers: `useAnalyticsGoals` and `useAnalyticsUsers`. `useStoreDocsByPaths`
 * lowers `loading` one commit before a grown path list subscribes, so the
 * subscribed key, as in `useKeptArtifacts`, keeps that commit loading. Paths
 * are compared as a set: reordered or repeated paths are the same list.
 */
import { useEffect, useState } from 'react';

import { useStoreDocsByPaths, type UseStoreDocsByPathsReturn } from '@/store';

export const useAnalyticsDocsByPaths = <T extends Record<string, unknown>>(
  paths: string[],
): UseStoreDocsByPathsReturn<T> => {
  const { data, loading, errors } = useStoreDocsByPaths<T>(paths);
  const subscriptionKey = [...new Set(paths)].sort().join('|');
  const [subscribedKey, setSubscribedKey] = useState(subscriptionKey);
  useEffect(() => setSubscribedKey(subscriptionKey), [subscriptionKey]);
  return {
    data,
    errors,
    loading: loading || subscribedKey !== subscriptionKey,
  };
};
